import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useAppData } from "store/appData";
import { cdnImageUrl, type ImageAnnotation } from "../api";
import { useDatasetDetail } from "./DatasetDetailContext";
import AnnotationOverlay from "./AnnotationOverlay";
import t from "../translations";

export default function DatasetImagePanel() {
  const { selectedImage, annotations, datasetTypeList, loading } =
    useDatasetDetail();
  const { objectTypes } = useAppData();
  const [imageError, setImageError] = useState<number | string | null>(null);
  // Pixel size of the image as it is currently rendered. The boxes are laid
  // out over a copy of that rect so they line up with an `object-fit: contain`
  // image without having to guess the letterboxing.
  const [renderedSize, setRenderedSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const fullSrc = selectedImage ? cdnImageUrl(selectedImage) : "";

  useEffect(() => {
    setImageError(null);
    setRenderedSize(null);
  }, [fullSrc]);

  const measure = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    setRenderedSize((prev) => {
      if (width <= 0 || height <= 0) return prev;
      if (prev && prev.width === width && prev.height === height) return prev;
      return { width, height };
    });
  }, []);

  const setImgRef = useCallback(
    (node: HTMLImageElement | null) => {
      // React calls this with null on unmount, so the disconnect here also
      // handles cleanup.
      observerRef.current?.disconnect();
      imgRef.current = node;
      if (node) {
        observerRef.current = new ResizeObserver(measure);
        observerRef.current.observe(node);
      }
    },
    [measure]
  );

  const visibleAnnotations = useMemo<ImageAnnotation[]>(() => {
    // Only show annotations whose object type is part of this dataset. An
    // empty type_list means "no restriction" (e.g. ad-hoc/test datasets).
    if (datasetTypeList.length === 0) return annotations;
    const allowed = new Set(datasetTypeList);
    return annotations.flatMap((annotation) => {
      const types = annotation.types.filter((t) =>
        allowed.has(t.object_type_id)
      );
      return types.length > 0 ? [{ ...annotation, types }] : [];
    });
  }, [annotations, datasetTypeList]);

  async function handleImageError() {
    if (!fullSrc) {
      setImageError("Source Missing");
      return;
    }
    try {
      const res = await fetch(fullSrc, {
        method: "HEAD",
        credentials: "include",
      });
      setImageError(res.ok ? null : res.status);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Network error";
      setImageError(message);
    }
  }

  function getErrorMessage(): string {
    if (imageError === 404) return t.ImageErrorNotFound;
    if (imageError === 401 || imageError === 403)
      return t.ImageErrorUnauthorized;
    return t.ImageErrorGeneric(imageError ?? "unknown");
  }

  if (loading && !selectedImage) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  if (!selectedImage) {
    return (
      <Container>
        <Typography color="text.secondary">{t.ImageNotFound}</Typography>
      </Container>
    );
  }

  return (
    <Container data-testid="datasetImagePanel.container">
      <Stack
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {imageError !== null ? (
          <Typography
            color="text.secondary"
            data-testid="datasetImagePanel.error"
          >
            {getErrorMessage()}
          </Typography>
        ) : (
          <>
            <Box
              component="img"
              ref={setImgRef}
              src={fullSrc}
              alt={selectedImage.name}
              onLoad={measure}
              onError={handleImageError}
              data-testid="datasetImagePanel.image"
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                userSelect: "none",
                display: "block",
              }}
            />
            {renderedSize && visibleAnnotations.length > 0 && (
              <AnnotationOverlay
                annotations={visibleAnnotations}
                objectTypes={objectTypes}
                width={renderedSize.width}
                height={renderedSize.height}
              />
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}

function Container({
  children,
  ...props
}: PropsWithChildren<{ "data-testid"?: string }>) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        height: "100%",
        bgcolor: "background.default",
        alignItems: "center",
        justifyContent: "center",
      }}
      {...props}
    >
      {children}
    </Box>
  );
}
